'use client'

import classnames from 'classnames'
import styles from './ThreeUpLocationCard.module.scss'
import SanityImage from '@/components/SanityImage/SanityImage'
import useI18n from '@/hooks/use-i18n'
import { getRestaurantAddressString } from '@/utils/format'
import RestaurantOpenClosedBlurbNewFormat from '@/components/RestaurantOpenClosedBlurbNewFormat/RestaurantOpenClosedBlurbNewFormat'
import Link from '@/components/Link/Link'
import Widont from '@/components/Widont/Widont'
import AnimateContainer from '@/components/AnimateContainer/AnimateContainer'
import Icon from '@/components/Icon/Icon'

type ThreeUpLocationCardProps = SanityPage & {
  className?: string
}

const ThreeUpLocationCard = ({ className, locationData, title, slug }: ThreeUpLocationCardProps) => {
  const { i18n, isValidI18nKey } = useI18n()

  const addressString = getRestaurantAddressString({
    streetAddress: locationData?.streetAddress,
    city: locationData?.city,
    state: (isValidI18nKey(locationData?.state) && i18n(locationData?.state)) || '',
    postalCode: locationData?.postalCode,
  })

  if (!locationData || !locationData?.featuredImage || !title) return null

  return (
    <AnimateContainer
      element="article"
      animationType="fadeIn"
      className={classnames(styles.ThreeUpLocationCard, className)}
    >
      <Link
        link={{
          label: '',
          linkType: 'internal',
          link: {
            _type: 'location',
            slug: slug.current,
          },
        }}
      >
        <div className={styles.imageContainer}>
          <SanityImage
            className={styles.image}
            image={locationData?.featuredImage}
            breakpoints={{
              tablet: {
                image: locationData?.featuredImage,
                width: 900,
                options: {
                  height: 1000,
                  fit: 'crop',
                },
              },
              xs: {
                image: locationData?.featuredImage,
                width: 800,
                options: {
                  height: 950,
                  fit: 'crop',
                },
              },
            }}
          />
        </div>
        <div className={styles.textContainer}>
          {locationData?.isNew && (
            <div className={styles.newLocationBadge}>
              <Icon name="newLocationBadgeFilled" />
            </div>
          )}
          <p className={styles.title}>
            <Widont text={title} />
          </p>
          <RestaurantOpenClosedBlurbNewFormat
            className={styles.openClosedBlurb}
            weeklyOpeningHours={locationData?.weeklyOpeningHours}
            timeZone={locationData?.timeZone}
            holidayHours={locationData?.holidayHours}
            isComingSoon={locationData?.isComingSoon}
            isTemporarilyClosed={locationData?.isTemporarilyClosed}
          />
          {locationData?.phoneNumber && (
            <p className={styles.phoneNumber}>{i18n('phoneNumberShort', { phoneNumber: locationData?.phoneNumber })}</p>
          )}
          {addressString && (
            <p className={styles.address}>
              {i18n('addressShort', {
                address: addressString,
              })}
            </p>
          )}
        </div>
      </Link>
    </AnimateContainer>
  )
}

ThreeUpLocationCard.displayName = 'ThreeUpLocationCard'

export default ThreeUpLocationCard
